/* Topbar — barre de titre et d'actions de l'application Factumis.
   Affiche : hamburger mobile | titre/fil d'Ariane | badge de conformité |
   barre de recherche (redirige vers /factures?q=…) | cloche notifications |
   bascule de thème (cycle système → clair → sombre, système = défaut).

   Le badge de conformité exige SIREN renseigné ET paStatus === 'active' :
   la connexion plateforme seule ne suffit pas — un SIREN absent afficherait
   « En règle » sur une org non provisionnée (voir commentaire inline). */
import { useState, type FormEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { Icon } from '../lib/icons';
import { TITLES, activeNavId, useGo } from './nav';
import { useMe } from '../lib/useMe';
import { useNotifications } from '../data/live';

export type ThemePref = 'system' | 'light' | 'dark';

// Icône et libellés du cycle de thème, indexés par la préférence COURANTE.
const THEME_UI: Record<ThemePref, { icon: 'contrast' | 'sun' | 'moon'; label: string; next: string }> = {
  system: { icon: 'contrast', label: 'Thème : système', next: 'passer en clair' },
  light: { icon: 'sun', label: 'Thème : clair', next: 'passer en sombre' },
  dark: { icon: 'moon', label: 'Thème : sombre', next: 'suivre le système' },
};

type TopbarProps = {
  themePref: ThemePref;
  cycleTheme: () => void;
  openMobile: () => void;
};

export function Topbar({ themePref, cycleTheme, openMobile }: TopbarProps) {
  const go = useGo();
  const { pathname } = useLocation();
  const [sq, setSq] = useState('');
  const t = TITLES[activeNavId(pathname)] || ['Factumis', null];
  const { me, loading: meLoading } = useMe();
  // Même critère que la page Conformité (ConformityPage : isOk = connected && siren) :
  // la connexion plateforme active NE SUFFIT PAS, le SIREN doit être renseigné. Sinon
  // le badge affichait « En règle » au-dessus d'une page « Configuration à compléter ».
  const isActive = me?.org?.paStatus === 'active' && Boolean(me?.org?.siren);
  // Indicateur cloche : point visible uniquement s'il y a des non-lues
  // (un seul fetch au montage — pas de polling).
  const { unread } = useNotifications();

  function submit(ev: FormEvent) {
    ev.preventDefault();
    const v = sq.trim();
    if (v) {
      go('invoices', { q: v });
      setSq('');
    }
  }

  return (
    <header className="topbar">
      <button className="iconbtn burger" onClick={openMobile} aria-label="Ouvrir le menu">
        <Icon name="menu" size={18} />
      </button>
      <div className="topbar__title">
        {t[1] ? <span className="crumb">{t[1]}&nbsp;&nbsp;/&nbsp;&nbsp;</span> : null}
        {t[0]}
      </div>
      <div className="topbar__spacer" />
      {!meLoading && (
        <button
          className={'confchip' + (isActive ? '' : ' confchip--warn')}
          onClick={() => go('conformity')}
          title="État de conformité"
        >
          <span className="dot" />
          {isActive ? 'En règle' : 'À configurer'}
        </button>
      )}
      <form className="searchbox" onSubmit={submit}>
        <Icon name="search" size={16} />
        <input
          placeholder="Rechercher une facture, un client…"
          aria-label="Rechercher"
          value={sq}
          onChange={(ev) => setSq(ev.target.value)}
        />
      </form>
      <button
        className="iconbtn"
        onClick={() => go('notifications')}
        aria-label={unread > 0 ? `Notifications (${unread} non lues)` : 'Notifications'}
        style={{ position: 'relative' }}
      >
        <Icon name="bell" size={18} />
        {unread > 0 && (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 7,
              right: 7,
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: 'var(--blue)',
              border: '2px solid var(--surface-card)',
            }}
          />
        )}
      </button>
      <button
        className="iconbtn"
        onClick={cycleTheme}
        aria-label={`${THEME_UI[themePref].label}, ${THEME_UI[themePref].next}`}
        title={`${THEME_UI[themePref].label} (cliquer pour ${THEME_UI[themePref].next})`}
      >
        <Icon name={THEME_UI[themePref].icon} size={18} />
      </button>
    </header>
  );
}
